import {
  ComponentType,
  CSSProperties,
  LegacyRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';
import ReactSelect, {
  ActionMeta,
  components as defaultComponents,
  IndicatorProps,
  MenuListComponentProps,
  OptionProps,
  OptionsType,
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueContainerProps,
  ValueType,
} from 'react-select';
import Async from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect, { Creatable } from 'react-select/creatable';

import Checkbox from '../Checkbox';
import Input from '../Input';
import Link from '../Link';
import { IndicatorSeparator } from '../Select';
import Spinner from '../Spinner';

import './MultiSelect.scss';

import {
  AnalyticsContext,
  handleAnalyticsEvent,
  TAnalyticsEventData,
} from '@dtci/dash/analytics';

import { TestMetadata } from '../types';

export enum SELECT_UNSELECT_ALL {
  SELECT_ALL = 'Select All',
  UNSELECT_ALL = 'Unselect All',
  SELECT_ALL_VALUE = 'select_all',
}

const ClearIndicator = <T extends OptionTypeBase>(
  props: IndicatorProps<T, true>,
): JSX.Element => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <div className="sb-multiselect__clear-indicator">CLEAR ALL</div>
    </div>
  );
};

const LoadingIndicator = (): JSX.Element => {
  return <Spinner className="sb-select__loading-indicator" size="small" />;
};

export type MultiSelectProps<T extends OptionTypeBase> = ReactSelectProps<
  T,
  true
> & {
  testMetadata?: TestMetadata;
};

// Add actions section next to menu list
const MenuList = <T extends OptionTypeBase>({
  selectProps,
  ...props
}: MenuListComponentProps<T, true>): JSX.Element => {
  const {
    inputRef,
    onInputChange,
    inputValue,
    onMenuInputFocus,
    showSelectAllOption,
    showInputPopout,
    innerListRef,
  } = selectProps;

  return (
    <div className="sb-muiltiselect_menu-list-wrapper" ref={innerListRef}>
      {showInputPopout && (
        <div className="sb-multiselect_popout-actions">
          <Input
            className="sb-multiselect_popout-input"
            inputRef={inputRef}
            value={inputValue}
            placeholder="Search..."
            width="auto"
            onChange={(e) => {
              // Fire native react-select input change
              if (onInputChange) {
                onInputChange(e.currentTarget.value, {
                  action: 'input-change',
                });
              }
            }}
            onMouseDown={(e) => {
              // Prevent react-select from taking back focus
              e.stopPropagation();
              (e.target as HTMLSelectElement).focus();
            }}
            onTouchEnd={(e) => {
              // Prevent react-select from taking back focus
              e.stopPropagation();
              (e.target as HTMLSelectElement).focus();
            }}
            onFocus={onMenuInputFocus}
          />
        </div>
      )}
      {showSelectAllOption && (
        <div
          className="sb-multiselect__select-all sb-link sb-link_underline_hover"
          onClick={selectProps.handleSelectAll}
        >
          <Link
            testMetadata={{
              'data-testid': 'selectAllLink',
              'data-qeid': 'selectAllLink',
            }}
            underline="hover"
          >
            {selectProps.selectAllText}
          </Link>
        </div>
      )}
      <defaultComponents.MenuList {...props} selectProps={selectProps} />
    </div>
  );
};

const MultiSelect = <
  T extends OptionTypeBase = {
    value: string;
    label: string;
    isDisabled?: boolean;
  },
>({
  onChange,
  className,
  isFetching,
  icon,
  testMetadata,
  iconContainerClassName,
  useMenuPortal = false,
  isError = false,
  customClear = true,
  components,
  customNoOptionsMessage,
  flush,
  creatable,
  onCreateOption,
  async,
  loadOptions,
  showCheckboxes,
  analyticsEventData,
  selectRef,
  isClearable = true,
  ...otherProps
}: MultiSelectProps<T> & {
  customClear?: boolean;
  icon?: ReactNode;
  iconContainerClassName?: string;
  useMenuPortal?: boolean;
  isError?: boolean;
  flush?: 'left' | 'right' | 'flush';
  creatable?: boolean;
  showCheckboxes?: boolean;
  analyticsEventData?: TAnalyticsEventData;
  onCreateOption?: (inputValue: string) => void;
  async?: boolean;
  customNoOptionsMessage?: string;
  loadOptions?: (
    inputValue: string,
    callback: (options: OptionsType<T>) => void,
  ) => void | Promise<T[]>;
  selectRef?:
    | LegacyRef<ReactSelect<T, true>>
    | LegacyRef<Async<T, true>>
    | LegacyRef<Creatable<T, true>>
    | LegacyRef<AsyncCreatableSelect<T, true>>;
}): JSX.Element | null => {
  const [values, setValues] = useState<ValueType<T, true>>(
    otherProps.value || [],
  );
  const [selectAllText, setSelectAllText] = useState<string>(
    values?.length === otherProps?.options?.length
      ? SELECT_UNSELECT_ALL.UNSELECT_ALL
      : SELECT_UNSELECT_ALL.SELECT_ALL,
  );

  const analyticsContext = useContext(AnalyticsContext);

  const onSelectChange = useCallback(
    (selections: ValueType<T, true>, actionMeta: ActionMeta<T>) => {
      if (!otherProps.isDisabled) {
        setValues(selections);

        onChange?.(selections, actionMeta);

        if (analyticsEventData) {
          handleAnalyticsEvent({
            analyticsContext,
            analyticsEventData,
            componentType: 'MultiSelect',
            actionName: 'onChange',
            label:
              otherProps?.['aria-label'] ||
              otherProps?.name ||
              className ||
              testMetadata?.['data-testid'] ||
              '',
            value:
              (selections !== null &&
                selections !== undefined &&
                Array.from(selections)) ||
              [],
          });
        }
      }
    },
    [
      analyticsContext,
      analyticsEventData,
      className,
      onChange,
      otherProps,
      testMetadata,
    ],
  );

  const handleSelectAll = useCallback(() => {
    const originalOptions = otherProps.options as OptionsType<T>;
    const actionMeta: ActionMeta<T> = { action: 'select-option' };
    if (SELECT_UNSELECT_ALL.SELECT_ALL === selectAllText) {
      onSelectChange(originalOptions, actionMeta);
    } else {
      onSelectChange([], actionMeta);
    }
  }, [onSelectChange, otherProps.options, selectAllText]);

  useEffect(() => {
    setSelectAllText(
      values?.length === otherProps?.options?.length
        ? SELECT_UNSELECT_ALL.UNSELECT_ALL
        : SELECT_UNSELECT_ALL.SELECT_ALL,
    );
  }, [values, otherProps]);

  useEffect(() => {
    setValues(otherProps.value);
  }, [otherProps.value]);

  const ValueContainer = useMemo<ComponentType<ValueContainerProps<T, true>>>(
    () =>
      icon
        ? ({ children, ...props }) => (
            <defaultComponents.ValueContainer {...props}>
              {!!children && (
                <div
                  className={classNames(
                    'sb-multiselect__icon-container',
                    iconContainerClassName,
                  )}
                >
                  {icon}
                </div>
              )}
              <div className="sb-multiselect__value-container-content">
                {children}
              </div>
            </defaultComponents.ValueContainer>
          )
        : defaultComponents.ValueContainer,
    [icon, iconContainerClassName],
  );

  const Option = <T extends OptionTypeBase>(
    props: OptionProps<T, true>,
  ): JSX.Element => (
    <defaultComponents.Option {...props}>
      <Checkbox
        checked={props.isSelected}
        option={{
          id: props.data.id,
          value: props.data.value,
          label: props.label,
        }}
      >
        {props.children}
      </Checkbox>
    </defaultComponents.Option>
  );

  const props: MultiSelectProps<T> = {
    menuPortalTarget:
      useMenuPortal && typeof window !== 'undefined' ? document.body : null,
    ...otherProps,
    isClearable,
    isLoading: isFetching,
    instanceId: otherProps.instanceId || otherProps.id,
    noOptionsMessage: customNoOptionsMessage
      ? () => customNoOptionsMessage
      : undefined,
    components: {
      LoadingIndicator,
      ValueContainer,
      IndicatorSeparator: (indicatorProps) => (
        <IndicatorSeparator {...indicatorProps} isClearable={isClearable} />
      ),
      MenuList,
      MultiValueRemove: otherProps.isDisabled
        ? () => null
        : defaultComponents.MultiValueRemove,
      ...(customClear && { ClearIndicator: ClearIndicator }),
      ...(showCheckboxes && { Option }),
      ...components,
    },
    isMulti: true,
    styles: {
      ...otherProps.styles,
      menu: (provided: CSSProperties) => ({
        ...provided,
        position: otherProps.expands ? 'static' : 'absolute',
        border: otherProps.borderless ? 0 : provided.border,
        boxShadow: otherProps.borderless ? 'none' : provided.boxShadow,
        '&:hover': {
          boxShadow: otherProps.borderless ? 'none' : provided.boxShadow,
        },
        zIndex: 20,
      }),
      menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 20 }),
    },
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
    tabSelectsValue: creatable || false,
    classNamePrefix: 'sb-multiselect',
    className: classNames(
      'sb-multiselect',
      {
        'sb-multiselect_error': isError,
        'sb-multiselect__flush-right': flush === 'right',
        'sb-multiselect__flush-left': flush === 'left',
        'sb-multiselect__flush': flush === 'flush',
        'sb-multiselect_with-checkboxes': showCheckboxes,
      },
      className,
    ),
  };

  if (creatable) {
    if (async && loadOptions) {
      return (
        <AsyncCreatableSelect<T, true>
          {...props}
          {...testMetadata}
          loadOptions={loadOptions}
          onCreateOption={onCreateOption}
          onChange={onSelectChange}
          icon={icon}
          ref={selectRef as LegacyRef<AsyncCreatableSelect<T, true>>}
        />
      );
    } else {
      return (
        <CreatableSelect<T, true>
          {...props}
          {...testMetadata}
          onCreateOption={onCreateOption}
          onChange={onSelectChange}
          icon={icon}
          ref={selectRef as LegacyRef<CreatableSelect<T, true>>}
        />
      );
    }
  } else {
    if (async && loadOptions) {
      return (
        <Async<T, true>
          {...props}
          {...testMetadata}
          loadOptions={loadOptions}
          onChange={onSelectChange}
          icon={icon}
          ref={selectRef as LegacyRef<Async<T, true>>}
        />
      );
    } else {
      return (
        <ReactSelect<T, true>
          {...props}
          {...testMetadata}
          value={values}
          selectAllText={selectAllText}
          handleSelectAll={handleSelectAll}
          onChange={onSelectChange}
          icon={icon}
          ref={selectRef as LegacyRef<ReactSelect<T, true>>}
        />
      );
    }
  }
};

export default MultiSelect;
